import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Languages, Loader2 } from "lucide-react";

const BG_IMAGE = "https://images.unsplash.com/photo-1764314138160-5f04f4a50dae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcGlzdCUyMGNsaW5pY3xlbnwwfHx8fDE3NzgxMDM2NzJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.has_active_subscription ? "/app" : "/billing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-zinc-200 bg-white/40 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl tracking-tight text-zinc-900">Clinico Translate</span>
        </div>

        <div className="max-w-lg">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-6">For clinicians</p>
          <h1 className="font-heading text-5xl xl:text-6xl tracking-tight leading-none text-zinc-900 mb-6">
            Communicate with every patient. In every language.
          </h1>
          <p className="text-lg text-zinc-600 leading-relaxed">
            A real-time translator built for occupational, speech, and physical therapists. Pre-loaded with discipline-specific phrases. Designed for the clinic floor.
          </p>
        </div>

        <p className="text-sm text-zinc-500">© 2026 Clinico Translate</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8">
          <h2 className="font-heading text-3xl tracking-tight text-zinc-900 mb-2">Sign in</h2>
          <p className="text-zinc-600 mb-8">Continue to your translator dashboard.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 text-base"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Password</Label>
                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2" data-testid="login-forgot-link">
                  Forgot?
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-base"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 text-base" data-testid="login-submit-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-zinc-600 mt-6 text-center">
            New to Clinico Translate?{" "}
            <Link to="/register" className="text-zinc-900 underline underline-offset-4 font-medium" data-testid="login-go-register-link">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
